import React from "react";
import { motion, AnimatePresence } from "framer-motion";

// Google Places 圖片網址產生器
const getGooglePhotoUrl = (photoReference, maxWidth = 400) => {
  if (!photoReference) return null;
  // DEBUG: log photoReference
  if (process.env.NODE_ENV !== "production") {
    console.debug("[RestaurantInfoCard] photoReference:", photoReference);
  }
  return `https://maps.googleapis.com/maps/api/place/photo?maxwidth=${maxWidth}&photoreference=${photoReference}&key=${
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""
  }`;
};

const DEFAULT_IMAGE =
  "data:image/svg+xml,%3Csvg width='250' height='160' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='250' height='160' fill='%2323272f'/%3E%3Ctext x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23aaa' font-size='18'%3ENo Image%3C/text%3E%3C/svg%3E";

// 餐廳資訊卡元件
const RestaurantInfoCard = ({
  name,
  address,
  phone,
  imageUrl,
  photoUrl, // 新增 photoUrl 支援
  photo_reference, // Google Places photo_reference
  rating,
  user_ratings_total,
  distance, // 公尺
  distance_meters, // 支援後端 distance_meters 欄位
  distance_km, // 支援後端 distance_km 欄位
  description,
  onClose, // 父層傳入 onClose
  animateClass, // 動畫 class
  show = true, // 控制顯示/隱藏動畫
}) => {
  // DEBUG: log all props
  if (process.env.NODE_ENV !== "production") {
    console.debug("[RestaurantInfoCard] props", {
      name,
      address,
      phone,
      imageUrl,
      photoUrl,
      photo_reference,
      rating,
      user_ratings_total,
      distance,
      distance_meters,
      distance_km,
      description,
    });
  }
  // 圖片優先順序：imageUrl > photoUrl > photo_reference > 預設
  const displayImageUrl =
    imageUrl || photoUrl || getGooglePhotoUrl(photo_reference) || DEFAULT_IMAGE;
  if (process.env.NODE_ENV !== "production") {
    console.debug("[RestaurantInfoCard] displayImageUrl", displayImageUrl);
  }
  // 距離顯示（優先 distance_meters，其次 distance，其次 distance_km）
  let distanceText = "距離未知";
  if (typeof distance_meters === "number") {
    distanceText =
      distance_meters >= 1000
        ? `${(distance_meters / 1000).toFixed(1)} 公里`
        : `${distance_meters} 公尺`;
  } else if (typeof distance === "number") {
    distanceText =
      distance >= 1000
        ? `${(distance / 1000).toFixed(1)} 公里`
        : `${distance} 公尺`;
  } else if (typeof distance_km === "number") {
    distanceText = `${distance_km} 公里`;
  }
  // 評分顯示
  const ratingDisplay =
    typeof rating === "number" ? (
      <span className="flex items-center gap-1">
        <svg width="16" height="16" fill="#fbbf24" viewBox="0 0 20 20">
          <path d="M10 15l-5.878 3.09 1.122-6.545L.488 6.91l6.561-.955L10 0l2.951 5.955 6.561.955-4.756 4.635 1.122 6.545z" />
        </svg>
        {rating.toFixed(1)}
      </span>
    ) : (
      <span className="text-gray-400">尚無評分</span>
    );
  const reviewsDisplay = user_ratings_total ? (
    <span className="text-gray-400">({user_ratings_total} 則評論)</span>
  ) : null;

  return (
    <AnimatePresence mode="wait">
      {show && (
        <motion.div
          key={name || "restaurant-info-card"}
          initial={{ opacity: 0, x: -20, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 20, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className={`w-[84vw] max-w-[250px] min-w-[120px] bg-[#181c23] rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col relative backdrop-blur-sm transition-all duration-300 ease-in-out ${
            animateClass ? animateClass : ""
          }`}
          style={{
            willChange: "opacity, transform",
            position: "fixed",
            top: "calc(env(safe-area-inset-top, 0px) + 65px)",
            left: "9px",
            zIndex: 1020,
          }}
        >
          {/* X 按鈕（右上角，貼齊卡片邊緣） */}
          {onClose && (
            <button
              className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-amber-600 text-white text-lg font-bold shadow hover:bg-amber-700 transition focus:outline-none focus:ring-2 focus:ring-amber-400 z-20"
              onClick={onClose}
              aria-label="關閉餐廳資訊"
              type="button"
              style={{ zIndex: 20 }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="5" y1="5" x2="15" y2="15" />
                <line x1="15" y1="5" x2="5" y2="15" />
              </svg>
            </button>
          )}
          <div
            className="w-full"
            style={{
              overflow: "hidden",
              borderRadius: "0.75rem 0.75rem 0 0",
              background: "#181c23",
            }}
          >
            <img
              src={displayImageUrl}
              alt={name}
              className="w-full h-40 object-cover object-center block"
              style={{ display: "block" }}
              loading="lazy"
              onError={(e) => (e.target.src = DEFAULT_IMAGE)}
            />
          </div>
          <div className="p-3 sm:p-4 flex-1 flex flex-col justify-between">
            <div>
              <h3 className="text-lg font-extrabold text-amber-300 mb-2 tracking-wide leading-tight">
                {name}
              </h3>
              <div className="flex items-center gap-2 text-gray-300 text-base mb-1">
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="#fbbf24"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M17.657 16.657L13.414 20.9a2 2 0 0 1-2.828 0l-4.243-4.243a2 2 0 0 1 0-2.828l4.243-4.243a2 2 0 0 1 2.828 0l4.243 4.243a2 2 0 0 1 0 2.828z" />
                  <path d="M15 11V7a3 3 0 0 0-6 0v4" />
                </svg>
                <span>{address}</span>
              </div>
              {/* 評分與評論數 */}
              <div className="flex items-center gap-2 text-amber-300 text-sm mb-1">
                {ratingDisplay}
                {reviewsDisplay}
              </div>
              {/* 距離顯示 */}
              <div className="text-xs text-gray-400 mb-1">
                距離：{distanceText}
              </div>
              {phone && (
                <div className="text-amber-400 text-base mb-1">
                  <a
                    href={`tel:${phone}`}
                    className="underline hover:text-amber-200 transition"
                  >
                    {phone}
                  </a>
                </div>
              )}
              {description && (
                <div className="bg-[#23272f] rounded-lg px-3 py-2 mt-2 text-gray-100 text-sm leading-relaxed shadow-inner border border-amber-100">
                  {description}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RestaurantInfoCard;
